/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC } from "react";

import dynamic from "next/dynamic";

import { COMPANY_NAME } from "lib/constants";
import { countriesType } from "lib/types";

interface LogoProps extends React.SVGProps<SVGSVGElement> {
  /**
   * ARIA label for the Logo component.
   */
  ariaLabel?: string;

  /**
   * Additional class name for the Logo component.
   */
  className?: string;

  /**
   * The locale for the Logo component.
   */
  locale?: countriesType;

  translations: {
    constitutionDayAlt?: string;
    logo: string;
    midsummerLogoAlt: string;
    prideLogoAlt: string;
  };

  /**
   * The variant of the Logo component.
   * Possible values are: 'pride', 'midsummer', 'regular', 'auto'.
   */
  variant?: "pride" | "midsummer" | "regular" | "auto";
}

interface SVGWrapperProps {
  Component: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  props: React.SVGProps<SVGSVGElement>;
}
const SVGWrapper: FC<SVGWrapperProps> = ({ Component, props }) => (
  <Component {...props} />
);

const DarkLogo = dynamic(() =>
  import("public/useit-dark.svg").then((mod) => ({
    default: (props: any) => (
      <SVGWrapper Component={mod.default} props={props} />
    ),
  })),
);
const DarkMidsummerLogo = dynamic(() =>
  import("public/useit-midsummer-dark.svg").then((mod) => ({
    default: (props: any) => (
      <SVGWrapper Component={mod.default} props={props} />
    ),
  })),
);
const LightMidsummerLogo = dynamic(() =>
  import("public/useit-midsummer.svg").then((mod) => ({
    default: (props: any) => (
      <SVGWrapper Component={mod.default} props={props} />
    ),
  })),
);
const DarkPrideLogo = dynamic(() =>
  import("public/useit-pride-dark.svg").then((mod) => ({
    default: (props: any) => (
      <SVGWrapper Component={mod.default} props={props} />
    ),
  })),
);
const LightPrideLogo = dynamic(() =>
  import("public/useit-pride.svg").then((mod) => ({
    default: (props: any) => (
      <SVGWrapper Component={mod.default} props={props} />
    ),
  })),
);
const LightLogo = dynamic(() =>
  import("public/useit.svg").then((mod) => ({
    default: (props: any) => (
      <SVGWrapper Component={mod.default} props={props} />
    ),
  })),
);

// Date range constants
const PRIDE_MONTH = 6; // June

// Special dates by country
type SpecialDates = {
  dk: Record<string, never>;
  fi: {
    midsummer: {
      dayOfWeek: number;
      endDay: number;
      month: number;
      startDay: number;
    };
  };
  no: {
    constitutionDay: {
      day: number;
      month: number;
    };
  };
  sv: {
    midsummer: {
      dayOfWeek: number;
      endDay: number;
      month: number;
      startDay: number;
    };
  };
};

const SPECIAL_DATES: SpecialDates = {
  sv: {
    // Sweden
    midsummer: {
      month: 6,
      startDay: 19,
      endDay: 25,
      dayOfWeek: 5, // Friday
    },
  },
  no: {
    // Norway
    constitutionDay: {
      month: 5,
      day: 17,
    },
  },
  dk: {}, // Denmark: No special dates currently
  fi: {
    // Finland
    midsummer: {
      month: 6,
      startDay: 20,
      endDay: 26,
      dayOfWeek: 6, // Saturday
    },
  },
} as const;

// Priority order for auto variant (higher index = higher priority)
const VARIANT_PRIORITY = {
  regular: 0,
  pride: 1,
  constitutionDay: 2,
  midsummer: 3,
} as const;

// Helper function to check if a date is within midsummer for a specific locale
const isMidsummer = (date: Date, locale: countriesType = "sv"): boolean => {
  // Only Sweden celebrates midsummer
  if (locale !== "sv") return false;

  const config = SPECIAL_DATES[locale].midsummer;
  const month = date.getMonth() + 1;
  const day = date.getDate();

  if (month !== config.month) return false;

  const isWithinDateRange = day >= config.startDay && day <= config.endDay;

  // Check both date range and day of week
  return isWithinDateRange && date.getDay() === config.dayOfWeek;
};

// Helper function to check if it's Constitution Day in Norway
const isConstitutionDay = (
  date: Date,
  locale: countriesType = "sv",
): boolean => {
  if (locale !== "no") return false;

  const config = SPECIAL_DATES.no.constitutionDay;
  return date.getMonth() + 1 === config.month && date.getDate() === config.day;
};

// Helper function to check if a date is during pride month
const isPrideMonth = (date: Date): boolean => {
  return date.getMonth() + 1 === PRIDE_MONTH;
};

export const Logo: FC<LogoProps> = ({
  ariaLabel,
  className,
  locale = "sv",
  translations,
  variant = "auto", // Default to Swedish locale
  ...other
}) => {
  const sharedProps = {
    viewBox: "0 0 351 352",
    width: 125,
    height: 125,
    role: "img",
  };
  const t = translations;

  const darkClasses = "hidden transition-all dark:block";
  const lightClasses = "block transition-all dark:hidden";

  const getLogo = () => {
    const today = new Date();

    // If variant is explicitly set, use that
    if (variant !== "auto") {
      switch (variant) {
        case "midsummer":
          return (
            <>
              <DarkMidsummerLogo
                {...sharedProps}
                aria-label={
                  ariaLabel || `${COMPANY_NAME} ${t.midsummerLogoAlt}`
                }
                className={`${darkClasses} ${className}`}
                {...other}
              />
              <LightMidsummerLogo
                {...sharedProps}
                aria-label={
                  ariaLabel || `${COMPANY_NAME} ${t.midsummerLogoAlt}`
                }
                className={`${lightClasses} ${className}`}
                {...other}
              />
            </>
          );
        case "pride":
          return (
            <>
              <DarkPrideLogo
                {...sharedProps}
                aria-label={ariaLabel || `${COMPANY_NAME} ${t.prideLogoAlt}`}
                className={`${darkClasses} ${className}`}
                {...other}
              />
              <LightPrideLogo
                {...sharedProps}
                aria-label={ariaLabel || `${COMPANY_NAME} ${t.prideLogoAlt}`}
                className={`${lightClasses} ${className}`}
                {...other}
              />
            </>
          );
        case "regular":
          return (
            <>
              <DarkLogo
                {...sharedProps}
                aria-label={ariaLabel || `${COMPANY_NAME} ${t.logo}`}
                className={`${darkClasses} ${className}`}
                {...other}
              />
              <LightLogo
                {...sharedProps}
                aria-label={ariaLabel || `${COMPANY_NAME} ${t.logo}`}
                className={`${lightClasses} ${className}`}
                {...other}
              />
            </>
          );
      }
    }

    // For auto variant, determine which logo to show based on priority
    const activeVariants = [
      { type: "regular" as const, active: true },
      { type: "pride" as const, active: isPrideMonth(today) },
      {
        type: "constitutionDay" as const,
        active: isConstitutionDay(today, locale),
      },
      { type: "midsummer" as const, active: isMidsummer(today, locale) },
    ].filter((v) => v.active);

    // Get the highest priority variant
    const highestPriorityVariant = activeVariants.reduce((highest, current) =>
      VARIANT_PRIORITY[current.type] > VARIANT_PRIORITY[highest.type]
        ? current
        : highest,
    );

    // Return the appropriate logo based on the highest priority variant
    switch (highestPriorityVariant.type) {
      case "midsummer":
        return (
          <>
            <DarkMidsummerLogo
              {...sharedProps}
              aria-label={ariaLabel || `${COMPANY_NAME} ${t.midsummerLogoAlt}`}
              className={`${darkClasses} ${className}`}
              {...other}
            />
            <LightMidsummerLogo
              {...sharedProps}
              aria-label={ariaLabel || `${COMPANY_NAME} ${t.midsummerLogoAlt}`}
              className={`${lightClasses} ${className}`}
              {...other}
            />
          </>
        );
      case "constitutionDay":
        // For now, use the regular logo for Constitution Day
        // TODO: Add Constitution Day specific logo when available
        return (
          <>
            <DarkLogo
              {...sharedProps}
              aria-label={
                ariaLabel || `${COMPANY_NAME} ${t.constitutionDayAlt || t.logo}`
              }
              className={`${darkClasses} ${className}`}
              {...other}
            />
            <LightLogo
              {...sharedProps}
              aria-label={
                ariaLabel || `${COMPANY_NAME} ${t.constitutionDayAlt || t.logo}`
              }
              className={`${lightClasses} ${className}`}
              {...other}
            />
          </>
        );
      case "pride":
        return (
          <>
            <DarkPrideLogo
              {...sharedProps}
              aria-label={ariaLabel || `${COMPANY_NAME} ${t.prideLogoAlt}`}
              className={`${darkClasses} ${className}`}
              {...other}
            />
            <LightPrideLogo
              {...sharedProps}
              aria-label={ariaLabel || `${COMPANY_NAME} ${t.prideLogoAlt}`}
              className={`${lightClasses} ${className}`}
              {...other}
            />
          </>
        );
      default:
        return (
          <>
            <DarkLogo
              {...sharedProps}
              aria-label={ariaLabel || `${COMPANY_NAME} ${t.logo}`}
              className={`${darkClasses} ${className}`}
              {...other}
            />
            <LightLogo
              {...sharedProps}
              aria-label={ariaLabel || `${COMPANY_NAME} ${t.logo}`}
              className={`${lightClasses} ${className}`}
              {...other}
            />
          </>
        );
    }
  };

  return getLogo();
};

export default Logo;
